import { Circle, Color, SVG, Svg } from "@svgdotjs/svg.js";
import { DateTime } from "luxon";
import { COPY_ICON } from "./copyIcon";
import { Entry, toEntry } from "./toEntry";

const KEY_TIMEZONES = "timezones";

let text: string = "";
let entries: Array<Entry> = [];
let timezones: Array<string> = [];
let timelineTimezone = "UTC";
let minMs = Number.MAX_VALUE;
let maxMs = -Number.MAX_VALUE;

const textarea = document.getElementById("textarea") as HTMLTextAreaElement;
const statuses = document.getElementById("statuses") as HTMLDivElement;
const rangeLabel = document.getElementById("range-label") as HTMLDivElement;
const formatSelect = document.querySelector(
  "#add-format select",
) as HTMLSelectElement;
const timelineStatuses = document.getElementById(
  "timeline-statuses",
) as HTMLDivElement;
const timelineTextarea = document.getElementById(
  "timeline-textarea",
) as HTMLTextAreaElement;
const timelineTimezoneSelect = document.querySelector(
  "#timeline-timezone select",
) as HTMLSelectElement;
const timelines = document.getElementById("timelines") as HTMLDivElement;
const svg = SVG("svg").size("100%", "100%") as Svg;
const l1 = svg.group(); // Timelines and circles
const l2 = svg.group(); // Verticle line and timestamps
const l3 = svg.group(); // Timezone background mask
const l4 = svg.group(); // Timezone background mask

enum FormatMethods {
  SECS = "toUnixInteger",
  MILLIS = "toMillis",
  ISO = "toISO",
  HTTP = "toHTTP",
  SQL = "toSQL",
  RFC2822 = "toRFC2822",
}

function formatName(method: FormatMethods) {
  switch (method) {
    case FormatMethods.HTTP:
      return "HTTP";
    case FormatMethods.ISO:
      return "ISO 8601";
    case FormatMethods.MILLIS:
      return "Unix (ms)";
    case FormatMethods.SECS:
      return "Unix (s)";
    case FormatMethods.RFC2822:
      return "RFC 2822";
    case FormatMethods.SQL:
      return "SQL";
    default:
      return "UNKNOWN";
  }
}

function serializeFormat(dt: DateTime, format: FormatMethods) {
  return format === FormatMethods.ISO
    ? dt[format]({ suppressMilliseconds: true }).toString()
    : dt[format]().toString();
}

function validEntryIndexes() {
  return entries.flatMap((entry, idx) => (entry.parsed ? [idx] : []));
}

function formatRange(ms: number) {
  const units = [
    { label: "day", ms: 24 * 60 * 60 * 1000 },
    { label: "hour", ms: 60 * 60 * 1000 },
    { label: "minute", ms: 60 * 1000 },
    { label: "second", ms: 1000 },
    { label: "millisecond", ms: 1 },
  ];

  for (const unit of units) {
    if (ms >= unit.ms || unit.ms === 1) {
      const value = Math.floor(ms / unit.ms);
      return `${value} ${unit.label}${value === 1 ? "" : "s"}`;
    }
  }

  return "0 milliseconds";
}

function colorSlotForEntry(entryIdx: number) {
  return validEntryIndexes().indexOf(entryIdx);
}

function colorForSlot(colorSlot: number) {
  const validCount = validEntryIndexes().length || 1;
  const h = (colorSlot * 360) / validCount;
  return new Color(h, 80, 65, "hsl");
}

function fillAndStroke(circle: Circle, entryIdx: number) {
  const colorSlot = colorSlotForEntry(entryIdx);
  if (colorSlot < 0) {
    return;
  }

  const isMoment = entries[entryIdx].moment;
  if (isMoment) {
    circle.fill(colorForSlot(colorSlot));
  } else {
    circle.fill("none").stroke({ color: colorForSlot(colorSlot).toString() });
  }
}

function statusBadge(valid: boolean, idx: number) {
  const container = document.createElement("div");
  container.className = "status-container";

  if (valid) {
    const svg = SVG().size("100%", "100%").addTo(container);
    const clipCircle = svg.circle("80%").attr("cx", "50%").attr("cy", "50%");
    const clip = svg.clip().add(clipCircle);
    fillAndStroke(
      svg
        .circle("80%")
        .attr("cx", "50%")
        .attr("cy", "50%")
        .stroke({ width: 6 })
        .clipWith(clip),
      idx,
    );
  } else {
    const span = document.createElement("span");
    span.innerHTML = "!";
    span.title = "Unparseable";
    container.appendChild(span);
  }

  return container;
}

function statusSpacer() {
  const container = document.createElement("div");
  container.className = "status-container";
  return container;
}

function updateStatus() {
  statuses.innerHTML = "";
  entries.forEach((e, idx) => {
    statuses.appendChild(statusBadge(e.parsed?.isValid === true, idx));
  });
}

function updateRangeLabel() {
  const parsedEntries = entries.flatMap((entry) =>
    entry.parsed ? [entry.parsed.toMillis()] : [],
  );

  if (parsedEntries.length <= 1) {
    rangeLabel.innerText = "Range";
    return;
  }

  const minParsed = Math.min(...parsedEntries);
  const maxParsed = Math.max(...parsedEntries);
  rangeLabel.innerText = `Range: ${formatRange(maxParsed - minParsed)}`;
}

function updateTimelineTextarea() {
  const projectedMillis = (entry: Entry) =>
    entry
      .parsed!.setZone(timelineTimezone, { keepLocalTime: !entry.moment })
      .toMillis();

  const sortedEntries = entries
    .filter((entry) => entry.parsed)
    .sort((a, b) => projectedMillis(a) - projectedMillis(b))
    .map((entry) => entry);

  const timelineLines: Array<string> = [];
  timelineStatuses.innerHTML = "";
  for (const [idx, entry] of sortedEntries.entries()) {
    timelineLines.push(entry.parsed?.comment ?? entry.raw);
    timelineStatuses.appendChild(statusBadge(true, entry.line));

    const nextEntry = sortedEntries[idx + 1];
    if (!nextEntry) {
      continue;
    }

    const gapMs = projectedMillis(nextEntry) - projectedMillis(entry);
    timelineLines.push(`  ... ${formatRange(gapMs)} ...`);
    timelineStatuses.appendChild(statusSpacer());
  }

  timelineTextarea.value = timelineLines.join("\n");
}

function parseTextArea() {
  text = textarea.value;
  entries = text.split("\n").map(toEntry);
  updateStatus();
  updateRangeLabel();
  updateTimelineTextarea();
}

textarea.addEventListener("input", (ev) => {
  refresh();
});

function drawTimelines() {
  l1.clear();
  l2.clear();
  l3.clear();
  l4.clear();

  const h = timelines.clientHeight / timezones.length;
  const w = timelines.clientWidth;

  minMs = Number.MAX_VALUE;
  maxMs = -Number.MAX_VALUE;

  const timezoneTimes: Array<Array<{ entryIdx: number; time: DateTime }>> =
    new Array(timezones.length);

  // Collect stats and create times adjusted to each timezone
  for (const [idx, tz] of timezones.entries()) {
    const y = idx * h + h / 2;

    // Put the timeline lines into a group for organizational purposes
    const tl = l1.group();
    const insetX = 2;
    const insetY = 8;

    tl.rect(w - insetX * 2, h - insetY)
      .move(insetX, idx * h + 1)
      .fill("white")
      .stroke({ color: "#d3d3d3", width: 2 });

    // Main horizontal
    tl.line(insetX * 4, y, w - insetX * 4, y).stroke({
      color: "#eee",
      width: 2,
    });

    // Arrows
    const arrowLen = 8;
    tl.line(insetX * 4, y, insetX * 4 + arrowLen, y - arrowLen).stroke({
      color: "#eee",
      width: 2,
    });
    tl.line(insetX * 4, y, insetX * 4 + arrowLen, y + arrowLen).stroke({
      color: "#eee",
      width: 2,
    });
    tl.line(w - insetX * 4, y, w - insetX * 4 - arrowLen, y - arrowLen).stroke({
      color: "#eee",
      width: 2,
    });
    tl.line(w - insetX * 4, y, w - insetX * 4 - arrowLen, y + arrowLen).stroke({
      color: "#eee",
      width: 2,
    });

    timezoneTimes[idx] = [];
    for (const [entryIdx, e] of entries.entries()) {
      if (!e.parsed) {
        continue;
      }

      const inTz = e.parsed.setZone(tz, { keepLocalTime: !e.moment });
      timezoneTimes[idx].push({ entryIdx, time: inTz });

      minMs = Math.min(minMs, inTz.toMillis());
      maxMs = Math.max(maxMs, inTz.toMillis());
    }
  }

  // Add padding
  const range = maxMs - minMs;
  minMs -= range * 0.1;
  maxMs += range * 0.1;

  // Draw actual circles
  for (const [idx, tzTimes] of timezoneTimes.entries()) {
    for (const { entryIdx, time: t } of tzTimes) {
      const y = idx * h + h / 2;

      fillAndStroke(
        l1
          .circle(20)
          .center(
            maxMs - minMs > 0
              ? (w * (t.toMillis() - minMs)) / (maxMs - minMs)
              : w / 2,
            y,
          )
          .stroke({ width: 4 })
          .attr("pointer-events", "all")
          .attr("cursor", "pointer")
          .on("click", (ev: Event) => {
            ev.stopPropagation();
            showModal({
              tz: timezones[idx],
              entry: entries[entryIdx],
            });
          }),
        entryIdx,
      );
    }
  }

  // Write timezone labels and center times
  for (const [idx, tz] of timezones.entries()) {
    const y = (idx + 1) * h - 28;
    // const timeStr = DateTime.fromMillis(minMs + range / 2, {
    //   zone: tz,
    // }).toISO();
    const label = l4
      .text(tz)
      .x(w / 2)
      .y(y)
      .font({ anchor: "middle", weight: "bold" });

    // Place box underneath matching background so crosshair doesn't interfere
    l3.rect(label.bbox().w + 10, label.bbox().h + 10)
      .center(label.bbox().cx, label.bbox().cy)
      .fill("white");
  }

  // Clear existing buttons
  document
    .querySelectorAll(".remove-button")
    .forEach((e) => e.parentNode!.removeChild(e));

  // Create buttons to remove timezones
  const buttonInsetX = 7;
  const buttonInsetY = 5;
  for (const [idx, tz] of timezones.entries()) {
    const y = idx * h + buttonInsetY;

    const button = document.createElement("button");
    button.onmousemove = (ev) => {
      ev.stopPropagation();
    };
    button.innerHTML = `<span>×</span>`;
    button.className = "remove-button";
    button.style.position = "absolute";
    button.style.top = `${y}px`;
    button.style.right = `${buttonInsetX}px`;
    button.onclick = () => {
      removeTimezone(tz);
    };
    timelines.appendChild(button);
  }
}

function sortTimezones() {
  const now = DateTime.now();
  timezones.sort((a, b) => {
    const offseta = now.setZone(a).offset;
    const offsetb = now.setZone(b).offset;
    if (offseta !== offsetb) {
      return offsetb - offseta;
    } else {
      return a.localeCompare(b);
    }
  });
}

function refresh() {
  sortTimezones();
  parseTextArea();
  drawTimelines();
}

function setDefaultTimelineTimezone() {
  const localTimezone = DateTime.now().zoneName;
  timelineTimezone = timezones.includes(localTimezone) ? localTimezone : "UTC";
  timelineTimezoneSelect.value = timelineTimezone;
  updateTimelineTextarea();
}

addEventListener("paste", (ev) => {
  // Allow "normal" paste behaviour to occur if you pasted inside textarea
  // `input` event will handle it
  if (ev.target === textarea) {
    return;
  }

  // Allow "global" paste behaviour if you pasted outside textarea

  // Prevent 'input' event from also firing
  // if we're pasting inside the input
  ev.preventDefault();
  let text = textarea.value;
  if (!text.endsWith("\n")) {
    text += "\n";
  }
  text += (ev as ClipboardEvent).clipboardData!.getData("text");
  textarea.value = text;
  refresh();
});

document.querySelector("body")!.onresize = refresh;

const now = DateTime.now().startOf("minute");
textarea.value = [
  `${now.toISO({ suppressMilliseconds: true })} # page load`,
  `${now.toISODate()} # midnight today`,
].join("\n");
refresh();

declare namespace Intl {
  type Key =
    | "calendar"
    | "collation"
    | "currency"
    | "numberingSystem"
    | "timeZone"
    | "unit";

  function supportedValuesOf(input: Key): string[];
}
const select = document.querySelector(
  "#add-timezone select",
)! as HTMLSelectElement;
for (const format of Object.values(FormatMethods)) {
  const option = document.createElement("option");
  option.text = formatName(format);
  option.value = format;
  formatSelect.appendChild(option);
}
formatSelect.value = FormatMethods.ISO;
for (const tz of Intl.supportedValuesOf("timeZone")) {
  const option = document.createElement("option");
  option.text = tz;
  option.value = tz;
  select.appendChild(option);

  const timelineOption = document.createElement("option");
  timelineOption.text = tz;
  timelineOption.value = tz;
  timelineTimezoneSelect.appendChild(timelineOption);
}
select.value = DateTime.now().zoneName;
timelineTimezoneSelect.value = timelineTimezone;

function addTimezone(tz: string) {
  removeTimezone(tz); // Clear if it's already there
  timezones.push(tz);
  localStorage.setItem(KEY_TIMEZONES, timezones.join());
  refresh();
}

function removeTimezone(tz: string) {
  timezones = timezones.filter((t) => t != tz);
  localStorage.setItem(KEY_TIMEZONES, timezones.join());
  refresh();
}

document.getElementById("add-button")!.onclick = () => {
  addTimezone(select.value);
};

timelineTimezoneSelect.onchange = () => {
  timelineTimezone = timelineTimezoneSelect.value;
  updateTimelineTextarea();
};

document.getElementById("add-format-button")!.onclick = () => {
  let text = textarea.value;
  if (text && !text.endsWith("\n")) {
    text += "\n";
  }
  text += serializeFormat(DateTime.now(), formatSelect.value as FormatMethods);
  textarea.value = text;
  refresh();
};

// Load previous timezones
if (localStorage.getItem(KEY_TIMEZONES)) {
  timezones = localStorage.getItem(KEY_TIMEZONES)!.split(",");
  setDefaultTimelineTimezone();
  refresh();
}
// No previous timezones -- make new ones
else {
  addTimezone("UTC");
  addTimezone(DateTime.local().zoneName);
  setDefaultTimelineTimezone();
}

let offsetX: number | undefined = undefined;
let offsetY: number | undefined = undefined;

timelines.onmouseover = (ev) => {
  offsetX = ev.offsetX;
  offsetY = ev.offsetY;
};

function snappedTimeAt(offsetX: number) {
  const w = timelines.clientWidth;

  // Get all ms values for entries
  const allMs: Set<number> = new Set();
  for (const e of entries) {
    for (const tz of timezones) {
      if (e.parsed) {
        const inTz = e.parsed.setZone(tz, { keepLocalTime: !e.moment });
        allMs.add(inTz.toMillis());
      }
    }
  }

  // Get a snapped ms based on physical proximity in px to known ms value
  // Default to a value based on pixel offset
  let snappedMs = (offsetX / w) * (maxMs - minMs) + minMs;
  let snappedX = offsetX;
  let isSnapped = false;
  for (const ms of allMs) {
    const msX = maxMs > minMs ? ((ms - minMs) / (maxMs - minMs)) * w : w / 2;
    if (Math.abs(offsetX - msX) <= 12) {
      snappedMs = ms;
      snappedX = msX;
      isSnapped = true;
    }
  }

  return {
    isSnapped,
    snappedMs,
    snappedX,
  };
}

function updateCrosshair() {
  if (offsetX === undefined || offsetY === undefined) {
    return;
  }

  const h = timelines.clientHeight / timezones.length;
  const { isSnapped, snappedMs, snappedX } = snappedTimeAt(offsetX);
  const hoveredTzIdx = Math.min(
    timezones.length - 1,
    Math.max(0, Math.floor(offsetY / h)),
  );

  l2.clear();

  const crosshairInset = 6;

  l2.line(
    snappedX,
    0,
    snappedX,
    timelines.clientHeight - crosshairInset,
  ).stroke({
    color: "#666",
  });
  l2.circle(10)
    .center(snappedX, hoveredTzIdx * h + h / 2)
    .fill("#bbb")
    .stroke({ color: "#666", width: 2 });

  const t = DateTime.fromMillis(snappedMs);

  const leftHalf = snappedX <= timelines.clientWidth / 2;
  // SVG text anchoring is not visually symmetric, so the right-aligned
  // labels need a slightly larger offset to match the left-side gap.
  const labelPadding = leftHalf ? 2 : 4;
  for (const [idx, tz] of timezones.entries()) {
    const rowTop = idx * h;
    const labelY = rowTop + 12;

    // Absolute time
    l2.text(t.setZone(tz).toISO({ suppressMilliseconds: true }))
      .x(snappedX + (leftHalf ? 1 : -1) * labelPadding)
      .y(labelY)
      .font({
        anchor: leftHalf ? "start" : "end",
      });

    // Relative time
    l2.text(t.setZone(tz).toRelative() ?? "")
      .x(snappedX + (leftHalf ? 1 : -1) * labelPadding)
      .y(labelY + 19)
      .font({
        anchor: leftHalf ? "start" : "end",
      });

    if (!isSnapped || idx !== hoveredTzIdx) {
      continue;
    }

    const snappedEntry = entries.find((e) => {
      if (!e.parsed?.comment) {
        return false;
      }

      const inTz = e.parsed.setZone(tz, { keepLocalTime: !e.moment });
      return inTz.toMillis() === snappedMs;
    });

    if (snappedEntry?.parsed?.comment) {
      l2.text(snappedEntry.parsed.comment)
        .x(snappedX + (leftHalf ? 1 : -1) * labelPadding)
        .y(labelY + 38)
        .font({
          anchor: leftHalf ? "start" : "end",
          weight: "bold",
        });
    }
  }
}

timelines.onmousemove = (ev) => {
  offsetX = ev.offsetX;
  offsetY = ev.offsetY;
  updateCrosshair();
};

timelines.onmouseout = () => {
  offsetX = offsetY = undefined;
  l2.clear();
};

timelines.onclick = (ev) => {
  if (timezones.length === 0) {
    return;
  }

  const rowHeight = timelines.clientHeight / timezones.length;
  const tzIdx = Math.min(
    timezones.length - 1,
    Math.max(0, Math.floor(ev.offsetY / rowHeight)),
  );
  const { snappedMs } = snappedTimeAt(ev.offsetX);

  showModal({
    tz: timezones[tzIdx],
    time: DateTime.fromMillis(snappedMs),
  });
};

// Modal stuff
const backdrop = document.querySelector(".modal-backdrop")! as HTMLDivElement;
const modal = document.querySelector(".modal")! as HTMLDivElement;

function showModal(
  displayInfo:
    | {
        tz: string;
        entry: Entry;
        time?: undefined;
      }
    | {
        tz: string;
        time: DateTime;
        entry?: undefined;
      }
    | undefined,
) {
  if (displayInfo) {
    const e = displayInfo.entry;
    const parsed = e?.parsed ?? displayInfo.time;
    const moment = e?.moment ?? false;

    const left = document.createElement("div");
    const badge = e ? statusBadge(true, e.line) : statusBadge(true, 0);
    left.appendChild(badge);

    const right = document.createElement("div");
    right.className = "modal-right";

    const title = document.createElement("div");
    title.className = "modal-title";
    title.innerText =
      e?.text ??
      parsed!.setZone(displayInfo.tz).toISO({ suppressMilliseconds: true })!;
    right.appendChild(title);

    const tz = document.createElement("div");
    tz.innerText = displayInfo.tz;
    right.appendChild(tz);

    const subtitle = document.createElement("div");
    subtitle.innerText = parsed?.toRelative() ?? "";
    right.appendChild(subtitle);

    // Output formats
    const table = document.createElement("table");
    for (const format of Object.values(FormatMethods)) {
      const row = document.createElement("tr");

      const name = document.createElement("td");
      name.innerText = formatName(format);
      row.appendChild(name);

      const value = document.createElement("td");
      const inTz = parsed!.setZone(displayInfo.tz, {
        keepLocalTime: !moment,
      });
      const text = serializeFormat(inTz, format);
      value.innerText = text;
      row.appendChild(value);

      const copy = document.createElement("td");
      const button = document.createElement("button");
      button.innerHTML = COPY_ICON;
      button.addEventListener("click", () => {
        navigator.clipboard.writeText(text);
      });
      copy.appendChild(button);
      row.appendChild(copy);

      table.appendChild(row);
    }
    right.appendChild(table);

    modal.replaceChildren(left, right);
  }

  const visibility = displayInfo ? "visible" : "hidden";
  backdrop.style.visibility = visibility;
  modal.style.visibility = visibility;
}

// Hide when clicking backdrop
backdrop.addEventListener("click", () => {
  showModal(undefined);
});

// Allow ESC to quit modal
document.querySelector("body")!.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape") {
    showModal(undefined);
  }
});
